'use client'

import Image from 'next/image'
import { useState } from 'react'
import { CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel'
import { CarouselBlockShell } from '@/components/ui/carousel-block-shell'
import { useCarouselCurrentIndex } from '@/components/ui/use-carousel-current-index'
import { cn } from '@/lib/utils'
import { generateHref } from '@/lib/generate-href'
import type { Icon, Media, Project } from '@/payload-types'

type ProjectLogo = {
  id: string
  name: string
  href: string
  logoUrl: string
  logoAlt: string
}

function getLogoData(project: Project): ProjectLogo | null {
  if (!project.logo || typeof project.logo === 'string') return null

  const media = project.logo as Media
  const size = media.sizes?.medium || media.sizes?.large
  const logoUrl = size?.url || media.url
  if (!logoUrl) return null

  return {
    id: project.id,
    name: project.name,
    href: typeof project.link === 'string' ? '#' : generateHref(project.link),
    logoUrl,
    logoAlt: media.alt || project.name,
  }
}

export function ProjectCarouselClient({
  projects,
  iconLeft,
  iconRight,
}: {
  projects: Project[]
  iconLeft?: (string | null) | Icon
  iconRight?: (string | null) | Icon
}) {
  const [api, setApi] = useState<CarouselApi>()
  const logos = projects.map(getLogoData).filter((logo): logo is ProjectLogo => logo !== null)
  const count = logos.length
  const current = useCarouselCurrentIndex(api, count)

  if (!logos.length) {
    return null
  }

  const renderedLogos = count === 3 ? [...logos, ...logos] : logos
  const slideBasisClass = cn(
    'basis-[62%] sm:basis-[42%]',
    count <= 2 ? 'md:basis-[34%] lg:basis-[26%]' : count === 3 ? 'md:basis-[28%] lg:basis-[22%]' : 'md:basis-[24%] lg:basis-[18%]',
  )

  return (
    <CarouselBlockShell
      ariaLabel="Projects carousel"
      setApi={setApi}
      opts={{ align: 'center', containScroll: false, loop: count > 1 }}
      iconLeft={iconLeft}
      iconRight={iconRight}
      count={count}
      current={current}
      onSelect={(index) => api?.scrollTo(index)}
    >
      <CarouselContent className="ml-0 py-2">
        {renderedLogos.map((logo, i) => (
          <CarouselItem key={`${logo.id}-${i}`} className={cn(slideBasisClass, 'px-1.5 sm:px-2')}>
            <a href={logo.href} className="block no-underline">
              <article className="theme-card mx-auto my-4 aspect-square w-full max-w-[220px] md:max-w-[240px] lg:max-w-[260px] border p-5">
                <div className="relative h-full w-full">
                  <Image
                    src={logo.logoUrl}
                    alt={logo.logoAlt}
                    fill
                    sizes="(max-width: 640px) 62vw, (max-width: 768px) 42vw, (max-width: 1024px) 28vw, 260px"
                    className="object-contain"
                  />
                </div>
                <span className="sr-only">{logo.name}</span>
              </article>
            </a>
          </CarouselItem>
        ))}
      </CarouselContent>
    </CarouselBlockShell>
  )
}
